import React, { useState, useRef } from "react";
import { ProfileImageUploadWrapper, ProfileImageUploadBox } from "./styled";

import { CloudArrowUpIcon, TrashIcon, CheckIcon } from "@phosphor-icons/react";
import Icon from "components/Icon/Icon";
import LoadingSpinner from "components/PageLoader/Spinner";

const ProfileImageUpload = ({
  label = "Profile Image",
  value,
  onChange,
  maxSize = 100 * 1024, // 100KB default
  acceptedFormats = ["image/png", "image/jpeg", "image/jpg"],
  acceptedFormatsText = "PNG, JPG",
  maxSizeText = "100kB",
  isPreview = false,
  uploadLoading = false,
  previewImage = null,
  onRemove
}) => {
  const fileInputRef = useRef(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState("");

  // File validation
  const validateFile = (file) => {
    if (!acceptedFormats.includes(file.type)) {
      setUploadError(`Please upload ${acceptedFormatsText} files only`);
      return false;
    }

    if (file.size > maxSize) {
      setUploadError(`File size must be less than ${maxSizeText}`);
      return false;
    }

    setUploadError("");
    return true;
  };

  // Handle file selection
  const handleFileSelect = (file) => {
    if (file && validateFile(file)) {
      setUploadedFile(file);
      onChange?.(file);
    }
  };

  // File input change handler
  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  // Browse button click handler
  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  // Delete file handler
  const handleDeleteFile = () => {
    setUploadedFile(null);
    setUploadError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onRemove?.();
  };

  const previewSrc = uploadedFile
    ? URL.createObjectURL(uploadedFile)
    : previewImage;
  const showPreview = Boolean(uploadedFile || (isPreview && previewImage));
  const fileName =
    uploadedFile?.name || (isPreview && previewImage ? "Current image" : "");

  return (
    <ProfileImageUploadWrapper>
      {label && <label>{label}</label>}
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedFormats.join(", ")}
        onChange={handleFileInputChange}
        style={{ display: "none" }}
      />

      {!showPreview ? (
        <ProfileImageUploadBox
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          isDragging={isDragging}
          onClick={handleBrowseClick}
        >
          <div className="upload-icon">
            <Icon
              component={CloudArrowUpIcon}
              color="var(--Neutral-40)"
              format="outline"
              weight="regular"
            />
          </div>
          <div className="upload-text">
            <span className="browse-text">Browse File</span>
            <span className="or-text"> or Drag and Drop</span>
          </div>
          <div className="file-info">
            {acceptedFormatsText} (max. {maxSizeText})
          </div>
        </ProfileImageUploadBox>
      ) : (
        <ProfileImageUploadBox className="file-preview">
          <div className="file-details">
            <div className="file-icon">
              {previewSrc ? (
                <img src={previewSrc} alt="preview" />
              ) : (
                <Icon
                  component={CloudArrowUpIcon}
                  color="var(--Neutral-40)"
                  format="outline"
                  weight="regular"
                />
              )}
            </div>
            <div className="file-info-text">
              {fileName && <div className="file-name">{fileName}</div>}
              {uploadLoading ? (
                <div className="file-status loading">
                  <LoadingSpinner
                    size={12}
                    color="var(--Neutral-40)"
                    text="Uploading..."
                    fontSize={12}
                    className="loading-spinner"
                  />
                </div>
              ) : (
                <div className="file-status">
                  <Icon
                    component={CheckIcon}
                    format="outline"
                    weight="regular"
                    color="var(--Success-100)"
                  />
                  <span>Uploaded</span>
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            className="delete-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteFile();
            }}
            disabled={uploadLoading}
          >
            <TrashIcon
              format="outline"
              weight="regular"
              color="var(--Error-100)"
            />
          </button>
        </ProfileImageUploadBox>
      )}

      {uploadError && <div className="error-message">{uploadError}</div>}
    </ProfileImageUploadWrapper>
  );
};

export default ProfileImageUpload;
